import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { token } from '@atlaskit/tokens';
import GlobalShortcuts, {
	type GlobalShortcutsItem,
	type GlobalShortcutsProps,
	productLinksWorkspaceCloudIdFilter,
	useOrderedAppShortcuts,
} from '@atlassian/global-shortcuts';
import type { ProductLink } from '@atlassian/navigation-bff-data-provider';

import { InvitedAppPopup } from './invited-app-popup';

// manage the allowed apps and their keys
const ALLOWED_APPS = ['jira', 'confluence'] as const;
type AllowedAppKey = (typeof ALLOWED_APPS)[number];

// map of the allowed app keys to their product types
const ALLOWED_APP_KEY_TO_PRODUCT_TYPE = {
	jira: 'JIRA_SOFTWARE',
	confluence: 'CONFLUENCE',
} as const;

export type ShortcutItemsConfig = Partial<Record<AllowedAppKey, GlobalShortcutsItem>>;

interface InvitedAppShortcutsProps
	extends Pick<GlobalShortcutsProps, 'cloudId' | 'orgId' | 'forceStatic'> {
	hoverDelayTimeMs?: number;
	shortcutItemsConfig: ShortcutItemsConfig;
}

export const InvitedAppShortcuts = ({
	cloudId,
	orgId,
	shortcutItemsConfig,
	hoverDelayTimeMs = 300,
	forceStatic = false,
}: InvitedAppShortcutsProps) => {
	// the popup shown state
	const [isPopupShown, setIsPopupShown] = useState(false);
	const closePopupTimerRef = useRef<NodeJS.Timeout | null>(null);

	// refs for the shortcut items
	const jiraShortcutRef = useRef<HTMLAnchorElement | null>(null);
	const confluenceShortcutRef = useRef<HTMLAnchorElement | null>(null);

	// Ref for the current shortcut item
	const [currentShortcutRef, setCurrentShortcutRef] =
		useState<React.MutableRefObject<HTMLAnchorElement | null>>();

	// map of the shortcut items to their refs
	const shortcutRefs: Record<
		AllowedAppKey,
		React.MutableRefObject<HTMLAnchorElement | null>
	> = useMemo(
		() => ({
			jira: jiraShortcutRef,
			confluence: confluenceShortcutRef,
		}),
		[jiraShortcutRef, confluenceShortcutRef],
	);

	// the allowed product types (matching the ProductLink.product.productType values)
	const allowedProductTypes: string[] = useMemo(
		() =>
			Object.keys(shortcutItemsConfig)
				.map((key) => ALLOWED_APP_KEY_TO_PRODUCT_TYPE[key as AllowedAppKey])
				.filter(Boolean),
		[shortcutItemsConfig],
	);

	const showPopup = useCallback(() => {
		setIsPopupShown(true);
	}, [setIsPopupShown]);

	const hidePopup = useCallback(() => {
		setIsPopupShown(false);
		setCurrentShortcutRef(undefined);
	}, [setIsPopupShown, setCurrentShortcutRef]);

	const clearClosePopupTimer = useCallback(() => {
		if (closePopupTimerRef.current) {
			clearTimeout(closePopupTimerRef.current);
			closePopupTimerRef.current = null;
		}
	}, []);

	const startClosePopupTimer = useCallback(() => {
		clearClosePopupTimer();
		closePopupTimerRef.current = setTimeout(() => {
			hidePopup();
		}, hoverDelayTimeMs);
	}, [clearClosePopupTimer, hoverDelayTimeMs, hidePopup]);

	/**
	 * @param targetRef - the ref to the shortcut item
	 * @returns a handler for the mouse over event on the shortcut item
	 */
	const handleMouseOverWithRef = useCallback(
		(targetRef: React.MutableRefObject<HTMLAnchorElement | null>) => () => {
			// clear the close popup timer to avoid closing the popup
			// when moving the mouse from the popup to the shortcut item
			clearClosePopupTimer();

			setCurrentShortcutRef(targetRef);
			showPopup();
		},
		[setCurrentShortcutRef, showPopup, clearClosePopupTimer],
	);

	/**
	 * @param targetRef - the ref to the shortcut item
	 * @returns a handler for the mouse leave event on the shortcut item
	 */
	const handleMouseLeaveWithRef = useCallback(
		(targetRef: React.MutableRefObject<HTMLAnchorElement | null>) => () => {
			if (currentShortcutRef?.current) {
				currentShortcutRef.current.style.backgroundColor = '';
			}

			startClosePopupTimer();
		},
		[currentShortcutRef, startClosePopupTimer],
	);

	/**
	 * @param targetRef - the ref to the shortcut item
	 * @returns a callback ref for a shortcut item
	 */
	const createShortcutRef = useCallback(
		(targetRef: React.MutableRefObject<HTMLAnchorElement | null>) =>
			(node: HTMLAnchorElement | null) => {
				// Cleanup previous element if it exists
				if (targetRef.current) {
					// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
					targetRef.current.removeEventListener('mouseover', handleMouseOverWithRef(targetRef));
					// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
					targetRef.current.removeEventListener('focus', handleMouseOverWithRef(targetRef));
					// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
					targetRef.current.removeEventListener('mouseleave', handleMouseLeaveWithRef(targetRef));
					// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
					targetRef.current.removeEventListener('blur', handleMouseLeaveWithRef(targetRef));
				}

				// Store the new element
				targetRef.current = node;

				// Attach listeners to new element
				if (node) {
					// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
					node.addEventListener('mouseover', handleMouseOverWithRef(targetRef));
					// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
					node.addEventListener('focus', handleMouseOverWithRef(targetRef));
					// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
					node.addEventListener('mouseleave', handleMouseLeaveWithRef(targetRef));
					// eslint-disable-next-line @repo/internal/dom-events/no-unsafe-event-listeners
					node.addEventListener('blur', handleMouseLeaveWithRef(targetRef));
				}
			},
		[handleMouseOverWithRef, handleMouseLeaveWithRef],
	);

	/**
	 * Handles the popup shown event
	 * @returns a handler for the popup shown event
	 */
	const handleOnMouseEnterPopup = useCallback(() => {
		// clear the close popup timer to avoid closing the popup too early
		clearClosePopupTimer();

		if (currentShortcutRef?.current) {
			// Get the parent element of the shortcut item
			const parentElement = currentShortcutRef.current.parentElement;

			if (parentElement) {
				// reference the background color variable from the design system
				// https://bitbucket.org/atlassian/atlassian-frontend-monorepo/src/4ff76dcfb3ce12b4344e5345be748ce472ac56e7/platform/packages/design-system/navigation-system/src/ui/menu-item/menu-item.tsx#lines-166
				parentElement.style.backgroundColor = token('elevation.surface.hovered');
			}

			// find the last child element (icon) of the parent element
			const lastChildElement = parentElement?.lastElementChild;
			if ((lastChildElement as HTMLElement)?.style) {
				// set visibility to hidden to the last child element
				(lastChildElement as HTMLElement).style.visibility = 'hidden';
			}
		}

		// ensure the popup is shown when mouse leaves the shortcut item and over the popup
		showPopup();
	}, [clearClosePopupTimer, showPopup, currentShortcutRef]);

	/**
	 * Handles the popup closed event
	 * @returns a handler for the popup closed event
	 */
	const handleOnMouseLeavePopup = useCallback(() => {
		if (currentShortcutRef?.current) {
			// Get the parent element of the shortcut item
			const parentElement = currentShortcutRef.current.parentElement;

			if (parentElement) {
				parentElement.style.backgroundColor = '';
			}

			const lastChildElement = parentElement?.lastElementChild;
			if ((lastChildElement as HTMLElement)?.style) {
				// set visibility to visible to the last child element
				(lastChildElement as HTMLElement).style.visibility = 'visible';
			}
		}
		startClosePopupTimer();
	}, [currentShortcutRef, startClosePopupTimer]);

	const shortcutItemsWithRef: ShortcutItemsConfig = useMemo(
		() =>
			Object.entries(shortcutItemsConfig).reduce((acc, [key, value]) => {
				if (ALLOWED_APPS.includes(key as AllowedAppKey)) {
					acc[key as AllowedAppKey] = {
						...value,
						// @ts-expect-error - callback ref is valid but not typed
						ref: createShortcutRef(shortcutRefs[key as AllowedAppKey]),
					};
				}
				return acc as ShortcutItemsConfig;
			}, {} as ShortcutItemsConfig),
		[shortcutItemsConfig, createShortcutRef, shortcutRefs],
	);

	// Sort shortcut items based on the ordered menu config
	const orderedShortcuts = useOrderedAppShortcuts(shortcutItemsWithRef);

	// Ensure we filter out other links except allowed and passed apps
	const invitedProductsFilter = useCallback(
		(workspaceCloudId: string) => (item: ProductLink, index: number, arr: ProductLink[]) => {
			return (
				allowedProductTypes.includes(item.product.productType) &&
				productLinksWorkspaceCloudIdFilter(workspaceCloudId)(item, index, arr)
			);
		},
		[allowedProductTypes],
	);

	const productLinksFilter = useMemo(
		() => (cloudId ? invitedProductsFilter(cloudId) : undefined),
		[cloudId, invitedProductsFilter],
	);

	useEffect(() => {
		return () => {
			clearClosePopupTimer();
		};
	}, [clearClosePopupTimer]);

	return (
		<>
			<GlobalShortcuts
				testId="global-invited-app-shortcuts"
				cloudId={cloudId}
				orgId={orgId}
				shortcutItems={orderedShortcuts}
				promoteSite
				product="confluence"
				forceStatic={forceStatic}
				productLinksFilter={productLinksFilter}
			/>
			{isPopupShown && currentShortcutRef && (
				<InvitedAppPopup
					trigger={currentShortcutRef}
					onMouseEnterPopup={handleOnMouseEnterPopup}
					onMouseLeavePopup={handleOnMouseLeavePopup}
				>
					{/* TODO: render the content based on the product and collaborators */}
					<div>InvitedAppShortcuts</div>
				</InvitedAppPopup>
			)}
		</>
	);
};
